import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, imgUrl, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Weight, Euro, Package, Calendar, MapPin, MessageSquare, Sprout, ShieldCheck, Handshake } from 'lucide-react';
import BatchFavoriteButton from '@/components/BatchFavoriteButton';
import { useRegisterGiulioContext } from '@/context/GiulioContext';
import MatchExplainBatch from '@/components/MatchExplainBatch';

const fmt = (n) => n != null ? new Intl.NumberFormat('it-IT').format(n) : '—';
const QUALITY_LABEL = {
  standard: 'Standard', biologico: 'Biologico', igp: 'IGP', dop: 'DOP', in_conversione: 'In conversione bio',
};
const PACKAGING_LABEL = {
  sfuso: 'Sfuso', cassette: 'In cassette', bins: 'Bins', sacchi: 'In sacchi', contenitori_industriali: 'Contenitori industriali',
};

export default function BatchDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [showContact, setShowContact] = useState(false);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/batches/${id}`).then(r => setB(r.data)).catch(()=>nav('/partite')); }, [id, nav]);

  // Registra la partita corrente nel contesto di Giulio
  useRegisterGiulioContext(b ? { kind: 'batch', data: b, label: b.title } : null);

  if (!b) return <div className="p-20 text-center text-muted-foreground">Caricamento…</div>;

  const imgs = b.images?.length ? b.images.map(imgUrl) : [];
  const isOwner = user?.user_id === b.owner_id;

  const sendMessage = async () => {
    if (!user) { nav('/login'); return; }
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post('/messages', { receiver_id: b.owner_id, content: `[Partita ${b.fruit_type}${b.variety?' · '+b.variety:''}] ${msg}` });
      toast.success('Messaggio inviato al venditore');
      setShowContact(false); setMsg('');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <Link to="/partite" className="text-sm text-muted-foreground link-underline">← Torna alle partite</Link>

      <div className="mt-6 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          {imgs.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-3">
              <img src={imgs[0]} alt="" className="w-full h-[380px] object-cover rounded-2xl md:col-span-2"/>
              {imgs.slice(1,4).map((u,i)=><img key={i} src={u} alt="" className="w-full h-[180px] object-cover rounded-2xl"/>)}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border h-64 grid place-items-center bg-secondary/20">
              <Sprout size={64} className="opacity-20" strokeWidth={1.2}/>
            </div>
          )}

          <div className="mt-8">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{b.fruit_type}</div>
            <h1 className="font-display text-3xl md:text-5xl tracking-tighter mt-1">{b.variety || b.fruit_type}</h1>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-chartreuse text-obsidian">
                {QUALITY_LABEL[b.quality_grade] || b.quality_grade}
              </span>
              {b.negotiable && (
                <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-secondary flex items-center gap-1">
                  <Handshake size={11}/> Trattabile
                </span>
              )}
              {b.location?.comune && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin size={12}/> {[b.location.comune, b.location.region].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat I={Weight} l="Quantità" v={`${fmt(b.quantity_quintals)} q`}/>
            <Stat I={Euro} l="Prezzo/q" v={`€ ${fmt(b.price_per_quintal)}`}/>
            <Stat I={Package} l="Packaging" v={PACKAGING_LABEL[b.packaging] || b.packaging || '—'}/>
            <Stat I={Calendar} l="Raccolto" v={b.harvest_date ? new Date(b.harvest_date).toLocaleDateString('it-IT') : '—'}/>
          </div>

          {b.description && (<>
            <h2 className="mt-12 font-display text-2xl">Descrizione</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed whitespace-pre-line">{b.description}</p>
          </>)}

          <h2 className="mt-12 font-display text-2xl">Disponibilità</h2>
          <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
            <div className="rounded-xl border border-border p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Disponibile dal</div>
              <div className="mt-1 font-medium">{b.available_from ? new Date(b.available_from).toLocaleDateString('it-IT') : 'Subito'}</div>
            </div>
            <div className="rounded-xl border border-border p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Fino al</div>
              <div className="mt-1 font-medium">{b.available_until ? new Date(b.available_until).toLocaleDateString('it-IT') : 'Fino ad esaurimento'}</div>
            </div>
          </div>
        </div>

        <aside className="md:sticky md:top-24 h-fit rounded-2xl border border-border p-6 bg-card">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Prezzo al quintale</div>
          <div className="font-display text-4xl leading-none mt-1">€ {fmt(b.price_per_quintal)}</div>
          <div className="mt-1 text-xs text-muted-foreground">{b.quantity_quintals} q disponibili</div>

          <div className="mt-6 rounded-xl bg-secondary p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Totale partita</div>
            <div className="font-display text-2xl">€ {fmt(b.total_price)}</div>
          </div>

          <div className="mt-6 space-y-2">
            {!isOwner && (
              <>
                <Button data-testid="batch-contact-btn" className="w-full rounded-full h-11" onClick={()=>{ if(!user){nav('/login');return;} setShowContact(true); }}>
                  <MessageSquare size={14} className="mr-2"/> Contatta il venditore
                </Button>
                <div className="flex gap-2">
                  <BatchFavoriteButton batchId={id} className="!w-auto !h-11 flex-1 px-3 gap-2"/>
                </div>
              </>
            )}
            {isOwner && (
              <Button variant="outline" className="w-full rounded-full h-11" onClick={async ()=>{
                if(!confirm('Rimuovere questa partita?')) return;
                await api.delete(`/batches/${id}`); toast.success('Partita rimossa'); nav('/partite');
              }}>Rimuovi partita</Button>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-border text-sm">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Venditore</div>
            <div className="flex items-center gap-2">
              <span className="font-medium">{b.owner_name}</span>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-accent-foreground bg-accent px-2 py-0.5 rounded-full">
                <ShieldCheck size={10}/> Verificato
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-2">Contatti via messaggistica interna, sicura e in tempo reale.</div>
            <div className="text-xs text-muted-foreground mt-2">Pubblicata: {new Date(b.created_at).toLocaleDateString('it-IT')}</div>
            <div className="text-xs text-muted-foreground">Visualizzazioni: {b.views || 0}</div>
          </div>
        </aside>
      </div>

      <Dialog open={showContact} onOpenChange={setShowContact}>
        <DialogContent>
          <DialogHeader><DialogTitle>Contatta {b.owner_name}</DialogTitle></DialogHeader>
          <Textarea
            rows={5}
            value={msg}
            onChange={e=>setMsg(e.target.value)}
            placeholder={`Ciao, sono interessato/a alla tua partita di ${b.fruit_type}${b.variety?' '+b.variety:''}. Vorrei sapere…`}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setShowContact(false)}>Annulla</Button>
            <Button onClick={sendMessage} disabled={busy || !msg.trim()}>{busy?'Invio…':'Invia'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ I, l, v }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground"><I size={12}/> {l}</div>
      <div className="mt-1 font-medium">{v}</div>
    </div>
  );
}
